import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from '@/components/icons/barrel';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  MarketsTableTabType,
  MarketsTableVariant,
} from '@/features/markets/types/markets';
import SYMBOL_CATEGORIES from '@/features/markets/utils/categories';
import { AssetType } from '@/types';

interface MarketsTableHeaderProps {
  tab: MarketsTableTabType;
  variant: MarketsTableVariant;
  search?: string;
  onTabChange: (tab: MarketsTableTabType) => void;
  onSearchChange: (query: string) => void;
}

export default function MarketsTableHeader(props: MarketsTableHeaderProps) {
  const { tab, variant, onTabChange, onSearchChange } = props;

  const { t } = useTranslation();

  const queryTimeout = useRef<NodeJS.Timeout>();
  const [query, setQuery] = useState<string>('');

  useEffect(() => {
    queryTimeout.current = setTimeout(() => onSearchChange(query), 500);
    return () => {
      clearTimeout(queryTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (props.search === undefined) return;
    setQuery(props.search);
  }, [props.search]);

  if (variant === 'page') {
    return (
      <Tabs
        className="flex flex-col"
        value={tab}
        onValueChange={(value) => {
          setQuery('');
          onSearchChange('');
          onTabChange(value as MarketsTableTabType);
        }}
      >
        <TabsList className="flex h-28 flex-col items-stretch border-b border-border md:h-[43px] md:flex-row">
          <div className="relative flex h-14 min-w-72 items-center border-border max-md:border-b md:h-full md:border-r">
            <Search className="absolute left-6 text-foreground" size={16} />
            <input
              value={query}
              type="text"
              placeholder={t('marketsPage.search')}
              className="relative z-10 w-full rounded-none bg-transparent pl-16 font-mono text-foreground placeholder:uppercase placeholder:text-vestgrey-100 focus:outline-none"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="h-14 flex-1 shrink-0">
            <div className="no-scrollbar relative h-full w-full overflow-x-auto overflow-y-hidden">
              <div className="absolute inset-0 flex h-full">
                <TabsTrigger
                  className="h-14 min-w-max border-t-0 border-b-transparent text-base normal-case max-lg:px-6 md:h-full"
                  value={'*'}
                >
                  {t('marketsPage.all')}
                </TabsTrigger>
                <TabsTrigger
                  className="h-14 min-w-max border-t-0 border-b-transparent text-base normal-case max-lg:px-6 md:h-full"
                  value={AssetType.STOCK}
                >
                  {t('marketsPage.stock')}
                </TabsTrigger>
                {Object.keys(SYMBOL_CATEGORIES).map((category) => (
                  <TabsTrigger
                    className="h-14 min-w-max border-t-0 border-b-transparent text-base normal-case max-lg:px-6 md:h-full"
                    value={category}
                    key={category}
                  >
                    {t(`marketsPage.categories.${category}`)}
                  </TabsTrigger>
                ))}
                <TabsTrigger
                  className="h-14 min-w-max border-t-0 border-b-transparent text-base normal-case max-lg:px-6 md:h-full"
                  value={AssetType.FOREX}
                >
                  {t('marketsPage.forex')}
                </TabsTrigger>
                <TabsTrigger
                  className="h-14 min-w-max border-t-0 border-b-transparent text-base normal-case max-lg:px-6 md:h-full"
                  value={AssetType.CRYPTO}
                >
                  {t('marketsPage.crypto')}
                </TabsTrigger>
              </div>
            </div>
          </div>
        </TabsList>
      </Tabs>
    );
  }

  return (
    <>
      <div className="flex h-14 items-center justify-between gap-3 border-b border-border bg-vestgrey-850 px-6 dark:bg-background">
        <div className="relative flex w-full items-center gap-1.5 text-foreground">
          <Search size={18} />
          <input
            value={query}
            type="text"
            placeholder={t('marketsTable.searchPlaceholder')}
            className="text-font w-full bg-vestgrey-850 p-2 font-mono placeholder:text-vestgrey-100 focus:outline-none dark:bg-background"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setQuery('');
          onSearchChange('');
          onTabChange(value as MarketsTableTabType);
        }}
      >
        <div className="relative h-12 w-full">
          <div className="absolute inset-0 h-full w-full">
            <ScrollArea className="w-full">
              <TabsList className="relative h-12 w-full min-w-max">
                <TabsTrigger
                  className="h-full min-w-max border-t-0 px-6 text-sm normal-case"
                  value={'*'}
                >
                  {t('marketsTable.all')}
                </TabsTrigger>
                <TabsTrigger
                  className="h-full min-w-max border-t-0 px-6 text-sm normal-case"
                  value={AssetType.STOCK}
                >
                  {t('marketsTable.stock')}
                </TabsTrigger>
                {Object.keys(SYMBOL_CATEGORIES).map((category) => (
                  <TabsTrigger
                    className="h-full min-w-max border-t-0 px-6 text-sm normal-case"
                    value={category}
                    key={category}
                  >
                    {t(`marketsTable.categories.${category}`)}
                  </TabsTrigger>
                ))}
                <TabsTrigger
                  className="h-full min-w-max border-t-0 px-6 text-sm normal-case"
                  value={AssetType.FOREX}
                >
                  {t('marketsTable.forex')}
                </TabsTrigger>
                <TabsTrigger
                  className="h-full min-w-max border-t-0 px-6 text-sm normal-case"
                  value={AssetType.CRYPTO}
                >
                  {t('marketsTable.crypto')}
                </TabsTrigger>
                <div className="h-full flex-1 border-b border-border" />
              </TabsList>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </div>
      </Tabs>
    </>
  );
}
